import L from 'leaflet';

/**
 * Forward projection WGS84 (lat, lng) → EPSG:3765 (HTRS96/TM).
 * Snyder's Transverse Mercator formulas on GRS80 ellipsoid.
 */
export function wgs84ToHTRS96TM(lat, lng) {
    var a = 6378137.0;
    var f = 1 / 298.257222101;
    var e2 = 2 * f - f * f;
    var ep2 = e2 / (1 - e2);
    var k0 = 0.9999;
    var lon0 = 16.5;
    var FE = 500000;

    var phi = lat * Math.PI / 180;
    var lam = (lng - lon0) * Math.PI / 180;

    var sinPhi = Math.sin(phi);
    var cosPhi = Math.cos(phi);
    var tanPhi = Math.tan(phi);
    var N = a / Math.sqrt(1 - e2 * sinPhi * sinPhi);
    var T = tanPhi * tanPhi;
    var C = ep2 * cosPhi * cosPhi;
    var A = lam * cosPhi;

    var e4 = e2 * e2;
    var e6 = e4 * e2;
    var M = a * (
        (1 - e2 / 4 - 3 * e4 / 64 - 5 * e6 / 256) * phi
        - (3 * e2 / 8 + 3 * e4 / 32 + 45 * e6 / 1024) * Math.sin(2 * phi)
        + (15 * e4 / 256 + 45 * e6 / 1024) * Math.sin(4 * phi)
        - (35 * e6 / 3072) * Math.sin(6 * phi)
    );

    var A2 = A * A;
    var A4 = A2 * A2;
    var x = k0 * (M + N * tanPhi * (A2 / 2 + (5 - T + 9 * C + 4 * C * C) * A4 / 24
        + (61 - 58 * T + T * T + 600 * C - 330 * ep2) * A4 * A2 / 720));
    var y = k0 * N * (A + (1 - T + C) * A2 * A / 6
        + (5 - 18 * T + T * T + 72 * C - 58 * ep2) * A4 * A / 120) + FE;

    return { x: x, y: y };
}

/**
 * Creates a custom Leaflet layer for external WMS servers that only support EPSG:3765.
 * Makes a single full-viewport GetMap request per view change instead of tiled requests.
 *
 * Supports two config shapes:
 *   - Nested (ISPU catalog extensionData): { url, wmsParams: { layers, version, format }, opacity, zIndex, ... }
 *   - Flat (WMS_LAYERS_COMPARABLE_SEARCH): { url, layers, version, format, layerHash, serviceId, opacity, ... }
 */
export function createExternalWMSLayer(layerConfig) {
    var isNested = !!layerConfig.wmsParams;
    var layers = isNested ? layerConfig.wmsParams.layers : layerConfig.layers;
    var version = isNested ? (layerConfig.wmsParams.version || '1.3.0') : (layerConfig.version || '1.3.0');
    var format = isNested ? (layerConfig.wmsParams.format || 'image/png') : (layerConfig.format || 'image/png');

    // For flat shape, build URL with layerHash and serviceId query params
    var baseUrl = layerConfig.url;
    if (!isNested && (layerConfig.layerHash || layerConfig.serviceId)) {
        var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
        var extraParams = [];
        if (layerConfig.layerHash) {
            extraParams.push('layerHash=' + encodeURIComponent(layerConfig.layerHash));
        }
        if (layerConfig.serviceId) {
            extraParams.push('serviceId=' + encodeURIComponent(layerConfig.serviceId));
        }
        baseUrl = baseUrl + sep + extraParams.join('&');
    }

    var ExternalWMSLayer = L.Layer.extend({
        initialize: function (options) {
            L.Util.setOptions(this, options);
            this._overlay = null;
            this._pendingOverlay = null;
        },

        onAdd: function (map) {
            this._map = map;
            this._updateImage();
            map.on('moveend', this._updateImage, this);
        },

        onRemove: function (map) {
            map.off('moveend', this._updateImage, this);
            if (this._pendingOverlay) {
                map.removeLayer(this._pendingOverlay);
                this._pendingOverlay = null;
            }
            if (this._overlay) {
                map.removeLayer(this._overlay);
                this._overlay = null;
            }
            this._map = null;
        },

        _updateImage: function () {
            var map = this._map;
            if (!map) return;

            var size = map.getSize();
            if (size.x === 0 || size.y === 0) return;

            var bounds = map.getBounds();
            var sw = wgs84ToHTRS96TM(bounds.getSouth(), bounds.getWest());
            var ne = wgs84ToHTRS96TM(bounds.getNorth(), bounds.getEast());

            var bbox = sw.y + ',' + sw.x + ',' + ne.y + ',' + ne.x;
            var separator = baseUrl.indexOf('?') === -1 ? '?' : '&';
            var url = baseUrl + separator + L.Util.getParamString({
                SERVICE: 'WMS',
                REQUEST: 'GetMap',
                VERSION: version,
                CRS: 'EPSG:3765',
                LAYERS: layers,
                STYLES: '',
                FORMAT: format,
                TRANSPARENT: 'true',
                WIDTH: size.x,
                HEIGHT: size.y,
                BBOX: bbox
            }, '', true);

            var imageBounds = L.latLngBounds(bounds.getSouthWest(), bounds.getNorthEast());

            // Discard any previous pending overlay that hasn't loaded yet
            if (this._pendingOverlay) {
                map.removeLayer(this._pendingOverlay);
                this._pendingOverlay = null;
            }

            this.fire('loading');

            var self = this;
            var newOverlay = L.imageOverlay(url, imageBounds, {
                opacity: layerConfig.opacity != null ? layerConfig.opacity : 1,
                zIndex: layerConfig.zIndex || 250,
                interactive: false
            });
            this._pendingOverlay = newOverlay;
            newOverlay.addTo(map);

            newOverlay.once('load', function () {
                // Only swap if this overlay is still the pending one
                if (self._pendingOverlay !== newOverlay) return;
                if (self._overlay && self._map) {
                    self._map.removeLayer(self._overlay);
                }
                self._overlay = newOverlay;
                self._pendingOverlay = null;
                self.fire('load');
            });

            newOverlay.once('error', function () {
                // Discard the failed overlay, keep the old one visible
                if (self._pendingOverlay === newOverlay) {
                    self._pendingOverlay = null;
                }
                if (self._map) {
                    self._map.removeLayer(newOverlay);
                }
                self.fire('load');
            });
        }
    });

    return new ExternalWMSLayer();
}
